// The request packet: everything a portal form, an email or a fax cover asks for
// — patient, member id, coverage, diagnoses, disciplines and units, window,
// ordering physician — as plain text for the clipboard.
//
// Atlas builds it and a person copies it. Nothing here emails, faxes or posts
// the packet anywhere, and it is fetched on open rather than kept: the text
// carries PHI and has no reason to outlive the dialog.

import React, { useEffect, useRef, useState } from "react";

import { fetchPacket } from "./api";

export default function PacketModal({ practiceId, flag, onClose }) {
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // null | "ok" | "failed"
  const areaRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchPacket(practiceId, flag.authId)
      .then((data) => {
        if (!cancelled) setText(data.text);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not build the request packet.");
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, flag.authId]);

  const copy = async () => {
    try {
      // navigator.clipboard needs a secure context; staging and production are
      // https, localhost counts as secure. Anywhere else the fallback is to
      // select the text so Cmd-C works.
      await navigator.clipboard.writeText(text);
      setCopied("ok");
    } catch (err) {
      setCopied("failed");
      if (areaRef.current) areaRef.current.select();
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div
        className="auth-modal"
        role="dialog"
        aria-label="Request packet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-modal-header">
          <h3>
            Request packet{flag.authNumber ? ` — ${flag.authNumber}` : ""}
          </h3>
          <button
            type="button"
            className="auth-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="auth-modal-body">
          {error ? (
            <div className="auth-message auth-message-error auth-message-inline">
              {error}
            </div>
          ) : text == null ? (
            <p className="auth-muted">Building the packet…</p>
          ) : (
            <>
              <textarea
                ref={areaRef}
                className="auth-input auth-textarea auth-packet-text"
                value={text}
                readOnly
                rows={14}
                aria-label="Packet text"
              />
              {copied === "ok" && (
                <p className="auth-field-hint">Copied to the clipboard.</p>
              )}
              {copied === "failed" && (
                <p className="auth-field-hint auth-field-hint-warn">
                  The browser would not copy it. The text is selected — press
                  Cmd-C (or Ctrl-C).
                </p>
              )}
              <p className="auth-modal-note">
                Atlas does not send this. Paste it into the payer's portal,
                email or fax, then use Mark requested.
              </p>
            </>
          )}
        </div>
        <div className="auth-modal-footer">
          <button type="button" className="auth-btn" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="auth-btn auth-btn-primary"
            onClick={copy}
            disabled={text == null}
          >
            Copy packet
          </button>
        </div>
      </div>
    </div>
  );
}
